import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield } from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { restaurantInfo } from '../data/mock';

const Section = ({ title, children }) => (
  <section className="mb-8">
    <h2 className="font-serif text-2xl font-bold text-[#1B4332] mb-3">{title}</h2>
    <div className="text-[#3D3D3D] leading-relaxed space-y-3 text-[15px]">{children}</div>
  </section>
);

const PrivacyPolicyPage = () => {
  const lastUpdated = '4 June 2026';

  return (
    <div className="min-h-screen bg-[#FAF7F2]">
      <Header />

      {/* Hero */}
      <section className="bg-[#1B4332] text-white py-16 px-4">
        <div className="max-w-3xl mx-auto">
          <Link
            to="/"
            data-testid="privacy-back-link"
            className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-6"
          >
            <ArrowLeft size={18} /> Back to home
          </Link>
          <div className="flex items-center gap-3 mb-3">
            <Shield size={32} className="text-[#C67C4E]" />
            <h1 className="font-serif text-4xl sm:text-5xl font-bold">Privacy Policy</h1>
          </div>
          <p className="text-white/80">Last updated: {lastUpdated}</p>
        </div>
      </section>

      {/* Body */}
      <main className="px-4 py-12">
        <article
          data-testid="privacy-policy-content"
          className="max-w-3xl mx-auto bg-white rounded-2xl shadow-sm p-8 sm:p-12 border border-[#1B4332]/10"
        >
          <p className="text-[#3D3D3D] leading-relaxed mb-8">
            La Pasteria Pizza Bar (“we”, “our”, “us”) is committed to protecting your privacy.
            This policy explains what personal data we collect when you use our website, mobile app
            or visit our restaurant, how we use it, and the rights you have under UK GDPR and the
            Data Protection Act 2018.
          </p>

          <Section title="1. Who we are">
            <p>
              <strong>La Pasteria Pizza Bar</strong><br />
              {restaurantInfo.address}<br />
              Phone: <a href={`tel:${restaurantInfo.phone}`} className="text-[#C67C4E] underline">{restaurantInfo.phone}</a><br />
              Email: <a href={`mailto:${restaurantInfo.email}`} className="text-[#C67C4E] underline">{restaurantInfo.email}</a>
            </p>
            <p>
              We are the “data controller” for the personal data we collect through our website,
              ordering app and in-restaurant systems.
            </p>
          </Section>

          <Section title="2. The information we collect">
            <p>We only collect what is needed to take your order, manage your booking and run the restaurant. This typically includes:</p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li><strong>Name, phone number and email address</strong> — so we can confirm your booking or food order.</li>
              <li><strong>Delivery address &amp; postcode</strong> — only if you place a delivery order.</li>
              <li><strong>Order details</strong> — items ordered, special instructions, total, payment method.</li>
              <li><strong>Booking details</strong> — date, time, party size, special requests, allergies.</li>
              <li><strong>Payment information</strong> — handled directly by our payment provider <strong>Stripe</strong>. We never see or store your full card number.</li>
              <li><strong>Technical data</strong> — IP address, browser type, device type and basic analytics so we can keep the website running smoothly.</li>
            </ul>
          </Section>

          <Section title="3. How we use your information">
            <p>We use your data only for the purposes you’d reasonably expect:</p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>To process your food orders and table bookings.</li>
              <li>To send confirmation, status updates and receipts by email.</li>
              <li>To take payment securely via Stripe.</li>
              <li>To contact you if there’s a problem with your order or booking.</li>
              <li>To meet our legal and tax obligations (e.g. keeping financial records).</li>
              <li>To improve our menu, hours and service based on aggregate (non-identifying) trends.</li>
            </ul>
            <p>We will <strong>never</strong> sell your data, and we don’t use it for marketing without your explicit consent.</p>
          </Section>

          <Section title="4. Legal basis for processing">
            <p>Under UK GDPR we rely on the following legal bases:</p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li><strong>Contract</strong> — to fulfil your order or booking.</li>
              <li><strong>Legal obligation</strong> — for tax and accounting records.</li>
              <li><strong>Legitimate interest</strong> — to run the restaurant safely, prevent fraud and improve our service.</li>
              <li><strong>Consent</strong> — for any optional marketing communications (always opt-in).</li>
            </ul>
          </Section>

          <Section title="5. Who we share your data with">
            <p>We only share data with trusted processors who help us deliver the service:</p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li><strong>Stripe</strong> — secure online payments (<a href="https://stripe.com/gb/privacy" target="_blank" rel="noopener noreferrer" className="text-[#C67C4E] underline">Stripe Privacy Policy</a>).</li>
              <li><strong>Resend</strong> — transactional emails (confirmations and updates).</li>
              <li><strong>Our hosting and database providers</strong> — to securely store and process your order/booking.</li>
              <li><strong>UK authorities</strong> — only if legally required (e.g. HMRC, fraud investigations).</li>
            </ul>
            <p>We do not transfer your personal data outside the UK/EEA unless that country has an adequate level of protection or we use approved safeguards.</p>
          </Section>

          <Section title="6. How long we keep your data">
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li><strong>Order and booking records</strong>: up to 7 years for accounting/tax purposes.</li>
              <li><strong>Email communications</strong>: up to 2 years after your last interaction with us.</li>
              <li><strong>Payment records</strong>: retained by Stripe according to their policy.</li>
            </ul>
            <p>Once these periods expire, the data is securely deleted or anonymised.</p>
          </Section>

          <Section title="7. Cookies">
            <p>
              We use a small number of essential cookies so the website works correctly (e.g. to keep your
              cart, remember your order type, and protect against abuse). We do not use third-party
              advertising or tracking cookies. You can disable cookies in your browser settings, but
              parts of the website (such as checkout) may stop working.
            </p>
          </Section>

          <Section title="8. Your rights">
            <p>Under UK GDPR you have the right to:</p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>Ask for a copy of the personal data we hold about you.</li>
              <li>Ask us to correct any inaccurate or incomplete data.</li>
              <li>Ask us to delete your data (where we are not legally required to keep it).</li>
              <li>Object to processing or ask us to restrict it.</li>
              <li>Withdraw consent for any marketing at any time.</li>
              <li>Lodge a complaint with the Information Commissioner’s Office (ICO) at <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer" className="text-[#C67C4E] underline">ico.org.uk</a>.</li>
            </ul>
            <p>
              To exercise any of these rights, email{' '}
              <a href={`mailto:${restaurantInfo.email}`} className="text-[#C67C4E] underline">{restaurantInfo.email}</a>{' '}
              or call <a href={`tel:${restaurantInfo.phone}`} className="text-[#C67C4E] underline">{restaurantInfo.phone}</a>.
              We aim to respond within 30 days.
            </p>
          </Section>

          <Section title="9. Children">
            <p>Our website and app are intended for adults. We do not knowingly collect personal data from anyone under 16. If you believe a child has provided us with personal data, please contact us and we will delete it.</p>
          </Section>

          <Section title="10. Security">
            <p>
              All data is sent over encrypted HTTPS connections. Payment data is handled directly by
              Stripe (a PCI-DSS Level 1 certified provider). Access to our admin systems is
              password-protected and restricted to authorised staff only.
            </p>
          </Section>

          <Section title="11. Updates to this policy">
            <p>
              We may update this policy from time to time. Significant changes will be reflected at
              the top of this page (the “last updated” date). Please check back occasionally.
            </p>
          </Section>

          <Section title="12. Contact us">
            <p>
              If you have any questions about this policy or your personal data, please reach out:
            </p>
            <p>
              <strong>La Pasteria Pizza Bar</strong><br />
              {restaurantInfo.address}<br />
              <a href={`mailto:${restaurantInfo.email}`} className="text-[#C67C4E] underline">{restaurantInfo.email}</a><br />
              <a href={`tel:${restaurantInfo.phone}`} className="text-[#C67C4E] underline">{restaurantInfo.phone}</a>
            </p>
          </Section>
        </article>
      </main>

      <Footer />
    </div>
  );
};

export default PrivacyPolicyPage;
